import React from 'react';
import PropTypes from 'prop-types';
import {List} from 'immutable';
import notifier from '@albert-io/notifier';
import {addToast} from '@albert-io/atomic';

import constants from 'client/constants';
import errorCodes from 'client/errorCodes';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import {genericMandarkRequest, invalidatePartialInterest} from 'resources/mandark.resource';
import {query, resource} from '@albert-io/json-api-framework/request/builder';

import GoogleContext from '../Google.context';

/**
 * Error handling for Google authentication using proper Honeybadger conventions
 */
const handleGoogleError = (error, context = '') => {
  // Don't show notifications for user-initiated actions
  if (error?.error === 'popup_closed_by_user' || error?.error === 'access_denied') {
    return;
  }

  // Show user-friendly error message
  addToast({
    title: 'Google Error',
    color: 'negative',
    message: 'There was a problem with Google services. Please try again.'
  });

  // Report to Honeybadger with proper structure
  notifier.notify(error, {
    message: `Google ${context} failed`,
    tags: 'GoogleProvider'
  });
};

/**
 * Google Context Provider
 * =======================
 *
 * This componenet wraps App.react.js and makes Google implementation available via context.
 *
 * The component's member methods will interface between Google's API and a state object.
 * We pass this state object to the context provider's value prop, where our context consumers
 * can then interact with it.  This can eventually replace GoogleClassroom.store.js.
 */

const scopes = `\
https://www.googleapis.com/auth/classroom.courses.readonly \
https://www.googleapis.com/auth/classroom.rosters.readonly \
https://www.googleapis.com/auth/classroom.profile.emails \
https://www.googleapis.com/auth/classroom.coursework.students \
profile`;

// Writing the token to state somewhere is probably irresponsible. We deliberately keep it in memory here instead.
let googleUserToken = null;

export default class GoogleProvider extends React.Component {
  static propTypes = {
    children: PropTypes.node
  };

  constructor() {
    super();

    this.state = {
      classrooms: List(),
      clearError: () => this.setState({error: null}),
      error: null,
      handleSignIn: this.handleSignIn,
      handleSignOut: this.handleSignOut,
      handleImportClassroom: this.handleImportClassroom,
      handleExportAssignment: this.handleExportAssignment,
      isRequestPending: false,
      isSignedIn: false,
      loggedInUserEmail: null
    };
  }

  /**
   * Lifecycle methods
   *
   * @param _
   * @param prevState
   */
  async componentDidUpdate(_, prevState) {
    // Handle sign-in
    if (this.state.isSignedIn && !prevState.isSignedIn) {
      try {
        // Using genericMandarkRequest here to bypass the cache (see https://github.com/albert-io/project-management/issues/2719)
        const classrooms = await genericMandarkRequest('get', this.getClassroomsQuery().done());
        this.setState({classrooms});
      } catch (error) {
        handleGoogleError(error, 'classroom-fetch');
      }
    }

    // Handle sign-out
    if (prevState.isSignedIn && !this.state.isSignedIn) {
      this.setState({classrooms: List()});
    }
  }

  handleSignIn = async () => {
    this.setState({isRequestPending: true});

    try {
      if (!global.gapi) {
        throw new Error('Google API not loaded');
      }
      await this._initGoogleClient();
    } catch (error) {
      this.setState({isRequestPending: false});
      handleGoogleError(error, 'sign-in');
    }
  };

  handleSignOut = () => {
    this.setState({isRequestPending: true});
    global.gapi.auth2
      .getAuthInstance()
      .signOut()
      .then(() => {
        const isSignedIn = !!global.gapi.auth2.getAuthInstance().isSignedIn.get();
        this.setState(
          {
            isRequestPending: false,
            isSignedIn,
            loggedInUserEmail: null
          },
          () => invalidatePartialInterest(this.getClassroomsQuery().done())
        );
        googleUserToken = null;
      })
      .catch((error) => {
        this.setState({isRequestPending: false});
        handleGoogleError(error, 'sign-out');
      });
  };

  handleExportAssignment = ({
    assignment,
    assignmentLink,
    googleCourseId,
    successCallback = () => {},
    errorCallback = () => {},
    classroomDueDate
  }) => {
    const assignmentState = assignment.isScheduled() ? 'DRAFT' : 'PUBLISHED';

    const scheduledTime = assignment.isScheduled()
      ? assignment.getStartDateFromSettings().utc().format()
      : null;

    const dueDate = classroomDueDate ?? assignment.getDueDateFromSettings();

    const payload = {
      courseId: googleCourseId,
      creationTime: assignment.getInsertedAt().utc().format(),
      description: assignment.getMessageFromSettings(),
      dueDate: {
        year: dueDate.utc().format('YYYY'),
        month: dueDate.utc().format('MM'),
        day: dueDate.utc().format('DD')
      },
      dueTime: {
        hours: dueDate.utc().format('HH'),
        minutes: dueDate.utc().format('mm')
      },
      materials: [
        {
          link: {
            url: assignmentLink,
            title: assignment.getName(),
            thumbnailUrl: assignmentLink
          }
        }
      ],
      scheduledTime: null,
      state: assignmentState,
      title: assignment.getName(),
      workType: 'ASSIGNMENT'
    };

    if (scheduledTime) {
      payload.scheduledTime = scheduledTime;
    }

    global.gapi.client.classroom.courses.courseWork
      .create(payload)
      .then(() => {
        successCallback();
        this.setState({isRequestPending: false});
      })
      .catch((error) => {
        this.setState({isRequestPending: false});
        errorCallback(error);
        handleGoogleError(error, 'assignment-export');
      });
  };

  handleImportClassroom = async ({callback = () => {}, classroomId, googleClassroomId}) => {
    const syncQuery = query()
      .mandarkEndpoint(['google_classroom_api', 'sync'])
      .customHeader({'google-classroom-api-token': googleUserToken})
      .customQuery({
        classroom_id: classroomId,
        course_id: googleClassroomId,
        issuer: masqueradeStore.getUserIdByMasqueradeState()
      })
      .done();

    this.setState({isRequestPending: true});

    try {
      await genericMandarkRequest('post', syncQuery);
      this.setState({isRequestPending: false});
      callback();
    } catch (error) {
      this.setState({isRequestPending: false});

      // Handle specific business errors that need special UI treatment
      const errorCode = error?.response?.body?.errors?.[0]?.code;
      if (errorCode === errorCodes.BAD_REQUEST.VALIDATION_ERROR.INVALID_EMAIL_DOMAIN) {
        this.setState({error: errorCode});
        return;
      }
      if (errorCode === errorCodes.FORBIDDEN.EXCEEDED_HARD_CAP_LIMIT) {
        this.setState({error: errorCode});
        return;
      }

      // All other errors get standard treatment
      handleGoogleError(error, 'classroom-import');
    }
  };

  /**
   * Albert API layer
   */
  getClassroomsQuery() {
    return resource('google_classroom_api_courses_v1')
      .mandarkEndpoint(['google_classroom_api', 'courses'])
      .customHeader({'google-classroom-api-token': googleUserToken});
  }

  /**
   * Simplified Google client initialization
   * @returns {Promise<void>} Resolves when the Google client is initialized and authenticated
   */
  _initGoogleClient = () => {
    return new Promise((resolve, reject) => {
      global.gapi.load('client:auth2', {
        callback: () => {
          this._handleGoogleAuth().then(resolve).catch(reject);
        },
        onerror: reject,
        timeout: 10000, // Increased timeout for slower connections
        ontimeout: () => reject(new Error('Google API timeout'))
      });
    });
  };

  _handleGoogleAuth = async () => {
    try {
      await global.gapi.client.init({
        clientId: constants.GCLOUD_CLIENT_ID,
        discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/classroom/v1/rest'],
        scope: scopes
      });

      const authInstance = global.gapi.auth2.getAuthInstance();
      const isSignedIn = authInstance.isSignedIn.get();

      let auth;
      if (isSignedIn) {
        auth = authInstance.currentUser.get();
      } else {
        auth = await authInstance.signIn({
          ux_mode: 'popup'
        });
      }

      googleUserToken = auth.getAuthResponse(true).access_token;
      this.setState({
        isSignedIn: true,
        isRequestPending: false,
        loggedInUserEmail: auth.getBasicProfile().getEmail()
      });
    } catch (error) {
      this.setState({
        isRequestPending: false,
        isSignedIn: false,
        loggedInUserEmail: null
      });

      // Reset Google auth state to allow retry
      try {
        global.gapi.auth2.getAuthInstance()?.disconnect();
      } catch (disconnectError) {
        console.warn('Could not disconnect Google auth instance:', disconnectError);
      }

      // Handle all authentication errors the same way
      handleGoogleError(error, 'authentication');
      throw error; // Re-throw for upstream handling
    }
  };

  render() {
    return (
      <GoogleContext.Provider value={this.state}>{this.props.children}</GoogleContext.Provider>
    );
  }
}
